// McDonaldsMapApp.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polygon,
  useMap,
} from "react-leaflet";
import { LatLngExpression, Icon, Map as LeafletMap } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import center from '@turf/center';
import MarkerClusterGroup from 'react-leaflet-cluster';

import { featureCollection, point, bbox, voronoi } from "@turf/turf";
import type { FeatureCollection, Point } from "geojson";

// @ts-ignore – GeoJSON wird ohne Typ geladen
import mcdGeoJSON from "./data/mcdonalds.json";
import mcdLogo from "./images/mcd_logo.svg";

type McD = {
  id: number;
  name: string;
  coords: [number, number];
  props: any;
};

function SetMapRef({ mapRef }: { mapRef: React.MutableRefObject<LeafletMap | null> }) {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;
  }, [map]);

  return null;
}

const goldenMIcon: Icon = new L.Icon({
  iconUrl: mcdLogo,
  iconSize: [30, 30],
  iconAnchor: [15, 30],
});

const mcDonalds: McD[] = (mcdGeoJSON as any).features
  .map((feature: any, index: number): McD | null => {
    let coords: [number, number] | null = null;

    if (feature.geometry.type === "Point") {
      coords = [feature.geometry.coordinates[1], feature.geometry.coordinates[0]];
    } else if (
      feature.geometry.type === "LineString" ||
      feature.geometry.type === "Polygon" ||
      feature.geometry.type === "MultiPolygon"
    ) {
      try {
        const centerPoint = center(feature);
        coords = [
          centerPoint.geometry.coordinates[1],
          centerPoint.geometry.coordinates[0],
        ];
      } catch (e) {
        coords = null;
      }
    }

    if (!coords) return null;

    return {
      id: index,
      name: feature.properties.name || "Unbenannt",
      coords,
      props: feature.properties,
    };
  })
  .filter(Boolean) as McD[];

export default function McDonaldsMapApp({ username }: { username: string }) {
  const [visited, setVisited] = useState<number[]>([]);
  const [voronoiPolygons, setVoronoiPolygons] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mapRef = useRef<LeafletMap | null>(null);

  const [isLoaded, setIsLoaded] = useState(false);

  const STORAGE_KEY = `mcd_visited_${username}`;

  // Laden beim Start aus localStorage
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setVisited(parsed);
        }
      } catch {
        // ignorieren
      }
    }
    setIsLoaded(true);
  }, [STORAGE_KEY]);

  // Speichern bei Änderung
  useEffect(() => {
    if (!isLoaded) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visited));
  }, [visited, isLoaded, STORAGE_KEY]);

  const toggleVisited = (id: number) => {
    setVisited((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const handleSearch = () => {
    const match = mcDonalds.find((mcd: McD) => {
      const fullAddress = `
        ${mcd.props["addr:street"] || ""}
        ${mcd.props["addr:housenumber"] || ""}
        ${mcd.props["addr:postcode"] || ""}
        ${mcd.props["addr:city"] || ""}
      `.toLowerCase();
      return fullAddress.includes(search.toLowerCase());
    });

    if (match && mapRef.current) {
      mapRef.current.flyTo(match.coords, 16);
    }
  };

  useEffect(() => {
    const points: FeatureCollection<Point> = featureCollection(
      mcDonalds.map((mcd: McD) =>
        point([mcd.coords[1], mcd.coords[0]], { id: mcd.id })
      )
    );

    const bounds = bbox(points);
    const voronoiResult = voronoi(points, { bbox: bounds });

    if (voronoiResult && voronoiResult.features.length > 0) {
      // IDs korrekt übernehmen
      voronoiResult.features.forEach((feature, index) => {
        const pointProps = points.features[index]?.properties;
        feature.properties = {
          ...feature.properties,
          id: pointProps?.id ?? index,
        };
      });

      setVoronoiPolygons(voronoiResult.features);
    }
  }, []);


  const visitedCount = visited.length;
  const totalCount = mcDonalds.length;
  const percentage = Math.round((visitedCount / totalCount) * 100);

  return (
    <>
      {/* Sidebar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: sidebarOpen ? 0 : "-260px",
          width: "250px",
          height: "100%",
          backgroundColor: "#fff",
          boxShadow: "0 0 10px rgba(0,0,0,0.3)",
          padding: "20px",
          transition: "right 0.3s ease-in-out",
          zIndex: 1000,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Fortschritt</h3>
        <p>
          {visitedCount} von {totalCount} besucht
        </p>
        <div style={{ height: "10px", background: "#eee", borderRadius: "5px" }}>
          <div
            style={{
              height: "10px",
              width: `${percentage}%`,
              background: "#c00",
              borderRadius: "5px",
            }}
          />
        </div>
        <p style={{ marginTop: "10px" }}>{percentage}%</p>
      </div>

      {/* Suchleiste */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          background: "#ffcc00",
          padding: "8px 12px",
          borderRadius: "8px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <input
          type="text"
          placeholder="Adresse suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: "6px 10px",
            border: "1px solid #c00",
            borderRadius: "4px",
            outline: "none",
            minWidth: "200px",
          }}
        />
        <button
          onClick={handleSearch}
          style={{
            backgroundColor: "#c00",
            color: "white",
            border: "none",
            borderRadius: "4px",
            padding: "6px 12px",
            cursor: "pointer",
          }}
        >
          Suchen
        </button>
        {/* Sidebar Toggle-Button */}
        <div
          onClick={() => setSidebarOpen(!sidebarOpen)}
          title="Fortschritt anzeigen"
          style={{
            cursor: "pointer",
            background: "#c00",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: "bold",
            fontSize: "18px",
            flexShrink: 0,
          }}
        >
          🍟
        </div>
      </div>

      <MapContainer
        center={[51.1657, 10.4515]}
        zoom={6}
        style={{ height: "100vh", width: "100%" }}
      >
        <SetMapRef mapRef={mapRef} />

        <TileLayer
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {voronoiPolygons.map((poly, idx) => (
          <Polygon
            key={idx}
            positions={poly.geometry.coordinates[0].map(
              (coord: [number, number]) =>
                [coord[1], coord[0]] as LatLngExpression
            )}
            pathOptions={{
              fillColor: visited.includes(poly.properties?.id)
                ? "green"
                : "#cccccc",
              fillOpacity: 0.3,
              color: "gray",
              weight: 1,
            }}
          />
        ))}
        <MarkerClusterGroup chunkedLoading>
          {mcDonalds.map((mcd: McD) => (
            <Marker position={mcd.coords} icon={goldenMIcon} key={mcd.id}>
              <Popup>
                <div>
                  <strong>{mcd.name}</strong>
                  <br />
                  {mcd.props["addr:street"] && (
                    <>
                      {mcd.props["addr:street"]} {mcd.props["addr:housenumber"]}
                      <br />
                      {mcd.props["addr:postcode"]} {mcd.props["addr:city"]}
                      <br />
                      <br />
                    </>
                  )}
                  {mcd.props.opening_hours && (
                    <>
                      <strong>Öffnungszeiten:</strong> {mcd.props.opening_hours}
                      <br />
                      <br />
                    </>
                  )}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${mcd.coords[0]},${mcd.coords[1]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Route planen
                  </a>
                  <br />
                  <br />
                  <button onClick={() => toggleVisited(mcd.id)}>
                    {visited.includes(mcd.id)
                      ? "Besucht ✅"
                      : "Als besucht markieren"}
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>
    </>
  );
}
